import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, Empty, Th, Td } from "../common/UI";
import { fmtDate, fmtDateTime } from "../../lib/format";

const COLORS = ["#0284C7", "#0F172A", "#38BDF8", "#64748B", "#0EA5E9", "#94A3B8", "#075985"];

export function KpiGrid({ kpis, linkWith }) {
  if (!kpis.length) return null;
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="report-kpis">
      {kpis.map((k) => {
        const body = (
          <>
            <div className="flex items-start justify-between gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{k.label}</span>
              <span title={k.definition} className="shrink-0 text-slate-300"><Info className="h-3.5 w-3.5" /></span>
            </div>
            <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900" data-testid={`kpi-value-${k.key}`}>{k.value ?? "—"}</div>
            <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">{k.basis === "snapshot" ? "Current snapshot" : "Reporting period"}</div>
          </>
        );
        const cls = "block rounded-md border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-sky-300";
        return k.link ? (
          <Link key={k.key} to={linkWith(k.link, k.basis)} className={cls} data-testid={`kpi-${k.key}`}>{body}</Link>
        ) : (
          <div key={k.key} className={cls} data-testid={`kpi-${k.key}`}>{body}</div>
        );
      })}
    </div>
  );
}

function ChartBody({ c }) {
  const axis = { tick: { fontSize: 10 } };
  if (c.type === "pie")
    return (
      <PieChart>
        <Pie data={c.data} dataKey={c.series[0].key} nameKey="name" outerRadius={90} innerRadius={50} paddingAngle={2}>
          {c.data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    );
  if (c.type === "line")
    return (
      <LineChart data={c.data}>
        <CartesianGrid stroke="#E2E8F0" vertical={false} /><XAxis dataKey="name" {...axis} /><YAxis allowDecimals={false} {...axis} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
        {c.series.map((s, i) => <Line key={s.key} dataKey={s.key} name={s.label} stroke={COLORS[i]} strokeWidth={2} dot={false} />)}
      </LineChart>
    );
  return (
    <BarChart data={c.data}>
      <CartesianGrid stroke="#E2E8F0" vertical={false} /><XAxis dataKey="name" {...axis} interval={0} angle={c.data.length > 6 ? -25 : 0} textAnchor={c.data.length > 6 ? "end" : "middle"} height={c.data.length > 6 ? 70 : 30} />
      <YAxis allowDecimals={false} {...axis} /><Tooltip />{c.series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
      {c.series.map((s, i) => <Bar key={s.key} dataKey={s.key} name={s.label} fill={COLORS[i]} stackId={c.type === "stacked" ? "a" : undefined} radius={c.type === "stacked" ? 0 : [3, 3, 0, 0]} />)}
    </BarChart>
  );
}

export const ReportCharts = ({ charts }) =>
  charts.length ? (
    <div className="mb-6 grid gap-4 lg:grid-cols-2" data-testid="report-charts">
      {charts.map((c) => (
        <Card key={c.id} title={c.title} testId={`chart-${c.id}`}>
          {c.data.length ? <div style={{ height: 260 }}><ResponsiveContainer><ChartBody c={c} /></ResponsiveContainer></div> : <Empty text="No data for this period." />}
        </Card>
      ))}
    </div>
  ) : null;

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtDateTime(v);
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtDate(v);
  return String(v);
};

function cell(col, r, linkWith) {
  const v = fmt(r[col.key]);
  if (col.link === "measure" && r.id) return <Link className="font-medium text-sky-700 hover:underline" to={`/measures/${r.id}`}>{v}</Link>;
  if (col.link === "session" && r.id) return <Link className="font-medium text-sky-700 hover:underline" to={`/sessions/${r.id}`}>{v}</Link>;
  if (col.link === "link" && r.link) return <Link className="font-medium text-sky-700 hover:underline" to={linkWith ? linkWith(r.link) : r.link}>{v}</Link>;
  return v;
}

export const ReportTable = ({ t, linkWith }) => (
  <Card title={`${t.title}${t.total > t.rows.length ? ` · showing ${t.rows.length} of ${t.total}` : ` · ${t.total}`}`} testId={`table-${t.id}`} className="mb-5">
    {t.rows.length ? (
      <div className="-m-5 overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead><tr>{t.columns.map((c) => <Th key={c.key}>{c.label}</Th>)}</tr></thead>
          <tbody>{t.rows.map((r, i) => <tr key={i} className="hover:bg-slate-50">{t.columns.map((c) => <Td key={c.key}>{cell(c, r, linkWith)}</Td>)}</tr>)}</tbody>
        </table>
      </div>
    ) : <Empty />}
    {t.note && <p className="mt-6 text-[11px] text-slate-500">{t.note}</p>}
  </Card>
);

export const Limitations = ({ items }) =>
  items?.length ? (
    <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="report-limitations">
      <div className="mb-1 font-semibold">Data notes and limitations</div>
      <ul className="list-disc space-y-0.5 pl-4">{items.map((n, i) => <li key={i}>{n}</li>)}</ul>
    </div>
  ) : null;

export const Definitions = ({ rep }) => (
  <details className="mb-6 rounded-md border border-slate-200 bg-white p-4 text-xs text-slate-600" data-testid="report-definitions">
    <summary className="cursor-pointer font-semibold text-slate-800">How these metrics are calculated</summary>
    <p className="mt-2"><b>Source:</b> {rep.source}</p>
    {rep.definitions?.map((d) => (
      <div key={d.name} className="mt-3 border-t border-slate-100 pt-2">
        <div className="font-semibold text-slate-800">{d.name}</div>
        <div>{d.description}</div>
        <div className="mt-1 grid gap-x-4 md:grid-cols-2">
          {[["Source", d.source], ["Included", d.inclusion], ["Excluded", d.exclusion], ["Date field", d.date_field], ["Grouping", d.grouping], ["Missing values", d.missing], ["Duplicates", d.duplicates]].map(([k, v]) => (
            <div key={k}><span className="text-slate-400">{k}:</span> {v}</div>
          ))}
        </div>
      </div>
    ))}
    {rep.kpis?.length > 0 && (
      <div className="mt-3 border-t border-slate-100 pt-2">
        <div className="font-semibold text-slate-800">KPI definitions</div>
        <ul className="mt-1 list-disc pl-4">{rep.kpis.map((k) => <li key={k.key}><b>{k.label}</b> ({k.basis}): {k.definition}</li>)}</ul>
      </div>
    )}
  </details>
);
